import type { ReactNode } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useAdminKey } from "@/hooks/use-auth";
import type { PatchChallengeInput } from "@/lib/api";
import { ApiError, adminApi } from "@/lib/api";
import { cn } from "@/lib/utils";

import { useChallenge } from "./use-challenge";

/**
 * A section that edits part of the challenge and saves itself.
 *
 * `patch` is computed by the section from its form against the challenge, so the footer
 * knows how many fields changed and sends only those. `problem` blocks saving with a reason.
 */
export function SectionCard({
  title,
  description,
  patch,
  problem,
  onSaved,
  onReset,
  children,
  footer,
}: {
  title: string;
  description?: ReactNode;
  patch: PatchChallengeInput;
  problem?: string | null;
  onSaved: () => void;
  onReset: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const { challenge } = useChallenge();
  const adminKey = useAdminKey();
  const changed = Object.keys(patch).length;

  async function save() {
    if (!adminKey) return;
    try {
      await adminApi.patchChallenge(challenge.id, patch, adminKey);
      toast.success("Saved");
      onSaved();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed", { duration: 10000 });
    }
  }

  return (
    <section className="bg-card text-card-foreground ring-foreground/10 rounded-xl ring-1">
      <div className="border-b px-5 py-4">
        <h2 className="text-base font-semibold">{title}</h2>
        {description ? <p className="text-muted-foreground mt-1 text-sm">{description}</p> : null}
      </div>
      <div className="grid gap-5 px-5 py-5">{children}</div>
      <div className="bg-muted/30 flex flex-wrap items-center justify-between gap-3 rounded-b-xl border-t px-5 py-3">
        <p className={cn("text-xs", problem ? "text-destructive" : "text-muted-foreground")}>
          {problem ?? (changed === 0 ? "No changes" : `${changed} field${changed === 1 ? "" : "s"} changed`)}
        </p>
        <div className="flex items-center gap-2">
          {footer}
          <Button variant="ghost" size="sm" onClick={onReset} disabled={changed === 0}>
            Reset
          </Button>
          <Button size="sm" onClick={save} disabled={changed === 0 || Boolean(problem)}>
            Save
          </Button>
        </div>
      </div>
    </section>
  );
}

